// frontend/src/pages/CancellationRefundPage.jsx
// Cancellation and Refund Policy page

import React from 'react';
import { motion } from 'framer-motion';

const CancellationRefundPage = () => {
  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Cancellation & Refund Policy
          </h1>

          <p className="text-gray-400 text-lg mb-8">
            Last updated: 21 January 2026
          </p>

          <div className="space-y-8 text-gray-300">

            {/* Intro */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <p>
                This Cancellation and Refund Policy (“Policy”) explains how refunds and
                cancellations are handled for all purchases, enrollments, and subscriptions
                made on the iStockly website or mobile application (“Platform”).
              </p>
              <p className="mt-3">
                By purchasing or enrolling in any course, mentorship, or subscription, you
                agree to the terms outlined below.
              </p>
            </section>

            {/* 1. General Policy */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                1. General Policy
              </h2>
              <p>
                All payments made to iStockly are non-refundable and non-transferable
                except under the specific conditions stated in this policy.
              </p>
              <p className="mt-2">
                Please read this carefully before making any payment or enrollment.
              </p>
            </section>

            {/* 2. Course Enrollment */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                2. Course Enrollment and Access
              </h2>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Your access to learning material is activated immediately.</li>
                <li>
                  Hence, cancellations after enrollment are not eligible for a refund,
                  since digital content and course access cannot be “returned.”
                </li>
              </ul>
              <p className="mt-3">
                All users are advised to review course details and demo content (if available)
                before making payment.
              </p>
            </section>

            {/* 3. Exceptions */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                3. Exceptions — Valid Refund Cases
              </h2>

              <p className="font-semibold text-white mt-2">a. Duplicate Payment</p>
              <p className="mt-1">
                If you are charged twice for the same course due to a technical or gateway
                error, you will be eligible for a 100% refund of the duplicate amount after
                verification.
              </p>

              <p className="font-semibold text-white mt-4">
                b. Technical Error on Payment
              </p>
              <p className="mt-1">
                If your payment is deducted but course access is not granted, please
                contact us within 48 hours. Once verified, access will be restored or a full
                refund will be processed.
              </p>

              <p className="font-semibold text-white mt-4">
                c. Course Cancellation by iStockly
              </p>
              <p className="mt-1">
                If iStockly cancels or discontinues a course or live program due to internal
                reasons, users will be eligible for:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li>A full refund, or</li>
                <li>
                  A credit transfer to another course of equal value (as per user’s choice).
                </li>
              </ul>
            </section>

            {/* 4. Non-Refundable */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                4. Non-Refundable Situations
              </h2>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>You changed your mind after purchase.</li>
                <li>You did not attend or complete the live sessions.</li>
                <li>
                  You are dissatisfied due to personal expectations or lack of practice.
                </li>
                <li>
                  You purchased a course during a discount, offer, or promotional event.
                </li>
                <li>
                  Your account was terminated for violating iStockly’s Terms & Conditions.
                </li>
                <li>
                  Delay or interruption caused by your internet connection, device issues,
                  or personal schedule.
                </li>
              </ul>
              <p className="mt-3">
                All users are responsible for reviewing course information and understanding
                the terms before enrolling.
              </p>
            </section>

            {/* 5. Cancellation */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                5. Cancellation Policy
              </h2>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>
                  Once a course, mentorship, or subscription is activated, cancellation
                  is not possible.
                </li>
                <li>
                  You may stop using the platform, but no partial or prorated refunds
                  will be issued.
                </li>
                <li>
                  Subscription cancellation will stop future renewals but will not refund
                  the current billing cycle.
                </li>
              </ul>
            </section>

            {/* 6. Refund Process */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                6. Refund Process & Timeline
              </h2>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>
                  Email us at <strong>support@istockly.in</strong> with your name,
                  registered email, payment receipt, and transaction ID.
                </li>
                <li>
                  Once verified, refunds will be processed within 7–10 working days
                  via the original payment method.
                </li>
              </ul>
              <p className="mt-3">
                Banks or payment providers may take additional time to reflect the refund.
              </p>
            </section>

            {/* 7. No Cash */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                7. No Cash Refunds
              </h2>
              <p>
                All refunds are processed digitally to the original payment source.
                Cash, cheque, or offline refunds are not provided under any circumstances.
              </p>
            </section>

            {/* 8. Dispute */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                8. Dispute Resolution
              </h2>
              <p>
                In case of a refund-related dispute, the decision of iStockly’s management
                team shall be final. All disputes are subject to the jurisdiction of
                Patna, Bihar, India.
              </p>
            </section>

            {/* 9. Contact */}
            <section className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-4">
                9. Contact Information
              </h2>
              <p>For refund or payment-related support:</p>
              <p className="mt-2 font-semibold">support@istockly.in</p>
            </section>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CancellationRefundPage;
